// MUNYA OS · § 16.bis · Feature flags + killswitches en frontend
// Cliente lee /api/v1/flags al iniciar SPA y guarda en context.
// useFeatureFlag(key) devuelve enabled boolean (default false hasta cargar).
// Refresh cada 60s + al recibir storage event (otros tabs).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

type FlagsMap = Record<string, boolean>;

interface FlagsContextValue {
  flags: FlagsMap;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const FlagsContext = createContext<FlagsContextValue>({
  flags: {},
  loaded: false,
  refresh: async () => {
    /* default no-op until provider monta */
  },
});

const REFRESH_INTERVAL_MS = 60_000;

export function FeatureFlagsProvider({ children }: { children: ReactNode }): ReactElement {
  const [flags, setFlags] = useState<FlagsMap>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/v1/flags', { credentials: 'same-origin' });
      if (!r.ok) {
        setLoaded(true); // marca cargado para no bloquear UI
        return;
      }
      const d = (await r.json()) as { flags?: FlagsMap };
      setFlags(d.flags ?? {});
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    // Sincroniza entre pestañas vía storage event + dentro de la misma pestaña vía
    // custom event (storage no se dispara en la pestaña que escribe).
    const onStorage = (e: StorageEvent): void => {
      if (e.key === 'mn:flags:bump') void refresh();
    };
    const onLocal = (): void => void refresh();
    window.addEventListener('storage', onStorage);
    window.addEventListener('mn:flags:refresh', onLocal);
    return () => {
      clearInterval(t);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('mn:flags:refresh', onLocal);
    };
  }, [refresh]);

  const value = useMemo(() => ({ flags, loaded, refresh }), [flags, loaded, refresh]);
  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

export function useFeatureFlag(key: string): boolean {
  const { flags } = useContext(FlagsContext);
  return flags[key] === true;
}

export function useKillswitch(
  module: 'chat' | 'track' | 'selection' | 'invoicing' | 'ai',
): boolean {
  const { flags } = useContext(FlagsContext);
  return flags[`killswitch_${module}`] === true;
}

export function useFlagsLoaded(): boolean {
  return useContext(FlagsContext).loaded;
}

/** Helper para forzar refresh de flags en otras pestañas + tab actual tras toggle admin */
export function bumpFlagsAcrossTabs(): void {
  try {
    localStorage.setItem('mn:flags:bump', String(Date.now()));
  } catch {
    /* ignore */
  }
  // storage event no se dispara en la misma pestaña que escribe · custom event sí
  window.dispatchEvent(new Event('mn:flags:refresh'));
}
